'use client';

import { useEffect, useRef, useState } from 'react';

import { useSetAtom } from 'jotai';
import { env } from 'next-runtime-env';

import { userIdAtom } from '@core/Atoms/User/UserId.atom';
import type { Tenant } from '@core/Entities/App/Tenants.entity';
import { clientLog } from '@core/Utils/Log/ClientLog';
import { AdTrackingPlugin } from '@snowplow/browser-plugin-ad-tracking';
import { ConsentPlugin } from '@snowplow/browser-plugin-consent';
import { enableFormTracking, FormTrackingPlugin } from '@snowplow/browser-plugin-form-tracking';
import { LinkClickTrackingPlugin } from '@snowplow/browser-plugin-link-click-tracking';
import { PerformanceTimingPlugin } from '@snowplow/browser-plugin-performance-timing';
import { WebVitalsPlugin } from '@snowplow/browser-plugin-web-vitals';
import { type BrowserTracker, enableActivityTracking, newTracker } from '@snowplow/browser-tracker';

import { snowplowReadyAtom } from '../Atoms/SnowplowReady.atom';
import { cmpVisibleToEvent } from '../Schemas/Quantcast/CmpVisible/CmpVisibleToEvent';
import { generateSnowplowAppId } from '../Utils/GenerateSnowplowAppId';
import { trackCustomEvent } from '../Utils/TrackCustomEvent';

type TcfApi = {
  (
    command: 'addEventListener' | 'removeEventListener',
    version: 2,
    callback: (tcData: any, success: boolean) => void,
  ): void;
  (command: 'ping', version: 2, callback: (pingReturn: { cmpLoaded: boolean }) => void): void;
};

// How long we're willing to wait for the real CMP *script* to load before concluding it's never
// coming and falling back to anonymous tracking. Distinct from waiting for the user's decision,
// which has no timeout - this only bounds the "is a real CMP present at all" check.
const CMP_LOAD_TIMEOUT_MS = 8000;
const CMP_LOAD_POLL_INTERVAL_MS = 200;

export const SnowplowScript = ({ tenant }: { tenant?: Tenant }) => {
  const setUserId = useSetAtom(userIdAtom);
  const setSnowplowReady = useSetAtom(snowplowReadyAtom);
  const [tracker, setTracker] = useState<BrowserTracker | null>(null);
  const trackerRef = useRef<BrowserTracker | null>(null);

  useEffect(() => {
    const getTcfApi = () => (window as typeof window & { __tcfapi?: TcfApi }).__tcfapi;

    // Snowplow trackers can't be re-created against the same trackerId, but they do support
    // toggling cookie-backed vs anonymous mode on an already-running instance. So we initialise
    // once - anonymously if no decision exists yet - and later "upgrade" the same tracker via
    // disableAnonymousTracking/enableAnonymousTracking rather than trying to init a second time.
    // This lets us start collecting (anonymously) as soon as the CMP banner is shown, which
    // cmp_visible needs, without losing the ability to switch into cookie mode once the user
    // actually decides.
    // isDecision distinguishes "we know whether tracking should be identified or not" (a real
    // consent decision, or a confirmed absence of any CMP to wait for) from "we're starting
    // anonymous collection early so cmp_visible can be recorded, but nothing has actually been
    // decided yet". Only the former should let page_view/custom events start firing - otherwise
    // page_view would fire the instant the CMP banner appears, before the user has done anything.
    const initTracker = (hasCookieConsent: boolean, isDecision: boolean) => {
      if (trackerRef.current) {
        if (hasCookieConsent) {
          trackerRef.current.disableAnonymousTracking({ stateStorageStrategy: 'cookie' });
        } else {
          trackerRef.current.enableAnonymousTracking({
            options: { withServerAnonymisation: true },
            stateStorageStrategy: 'none',
          });
        }
        if (isDecision) setSnowplowReady(true);
        return;
      }

      const collectorUrl = `${window.location.origin}`.includes('http://localhost:300')
        ? 'localhost:9090'
        : env('NEXT_PUBLIC_SNOWPLOW_COLLECTOR_URL')!;

      if (!collectorUrl) {
        console.error('No collector URL for Snowplow found');
        return;
      }

      const initializedTracker = newTracker('sp_i15', collectorUrl, {
        appId: generateSnowplowAppId({ app: tenant }),
        plugins: [
          LinkClickTrackingPlugin(),
          FormTrackingPlugin(),
          AdTrackingPlugin(),
          WebVitalsPlugin(),
          PerformanceTimingPlugin(),
          ConsentPlugin(),
        ],
        ...(hasCookieConsent
          ? { stateStorageStrategy: 'cookie' }
          : { anonymousTracking: { withServerAnonymisation: true }, stateStorageStrategy: 'none' }),
      });

      trackerRef.current = initializedTracker;
      setTracker(initializedTracker);

      enableActivityTracking({ minimumVisitLength: 30, heartbeatDelay: 10 });
      enableFormTracking();

      if (isDecision) setSnowplowReady(true);
    };

    const callback = (tcData: any) => {
      if (!tcData) return;

      // The banner becoming visible isn't a consent decision, but it's the point at which we
      // start collecting anonymously - both to record cmp_visible and per the "no cost to
      // waiting" anonymous default - so it does trigger the (anonymous) init. It must not mark
      // Snowplow as "ready" though - page_view/custom events still wait for a real decision.
      if (tcData.eventStatus === 'cmpuishown') {
        initTracker(false, false);
        trackCustomEvent({ event: cmpVisibleToEvent(), context: [] });
        return;
      }

      // Only 'useractioncomplete' (fresh decision) or 'tcloaded' with a stored string
      // (returning visitor) represent an actual consent decision worth acting on.
      const isConsentDecision =
        tcData.eventStatus === 'useractioncomplete' ||
        (tcData.eventStatus === 'tcloaded' && Boolean(tcData.tcString));
      if (!isConsentDecision) return;

      const hasCookieConsent =
        tcData.purpose?.consents?.[1] === true && tcData.purpose?.consents?.[8] === true;
      initTracker(hasCookieConsent, true);
    };

    if (!getTcfApi()) {
      // No CMP script present at all (blocked, ad-blocker, script error) - there's nothing to
      // ever wait for, so fall back to anonymous tracking rather than never tracking at all.
      initTracker(false, true);
      return;
    }

    // The IAB TCF stub installs window.__tcfapi synchronously, before the real CMP script has
    // loaded - it just queues calls like 'addEventListener' until the real CMP arrives and
    // flushes the queue. Per the TCF spec the real CMP, once loaded, *overwrites*
    // window.__tcfapi with its own implementation - so we must always read window.__tcfapi
    // fresh (never hold onto a captured reference), otherwise every call keeps hitting the
    // original stub forever. The stub's own 'ping' handler hardcodes cmpLoaded: false - it has
    // no way to know the real CMP has since loaded - so a stale reference would report "not
    // loaded" indefinitely even after the real CMP is active.
    let settled = false;
    let pollTimer: number | undefined;

    const pingForRealCmp = (isFinalAttempt = false) => {
      const tcfapi = getTcfApi();
      if (!tcfapi) return;

      tcfapi('ping', 2, pingReturn => {
        if (settled) return;

        if (pingReturn.cmpLoaded) {
          settled = true;
          window.clearTimeout(pollTimer);
          getTcfApi()?.('addEventListener', 2, callback);
          return;
        }

        if (isFinalAttempt) {
          settled = true;
          initTracker(false, true);
          return;
        }

        pollTimer = window.setTimeout(pingForRealCmp, CMP_LOAD_POLL_INTERVAL_MS);
      });
    };

    const deadlineTimer = window.setTimeout(() => {
      window.clearTimeout(pollTimer);
      pingForRealCmp(true);
    }, CMP_LOAD_TIMEOUT_MS);

    pingForRealCmp();

    return () => {
      settled = true;
      window.clearTimeout(deadlineTimer);
      window.clearTimeout(pollTimer);
      getTcfApi()?.('removeEventListener', 2, callback);
    };
  }, []);

  useEffect(() => {
    if (!tracker) return;

    const snowplowUserId = tracker?.getDomainUserId() as unknown as string | null;

    // if we already have a userId then it's likely been resolved
    setUserId(userId => {
      const clientUserId = userId || snowplowUserId;

      tracker.setUserId(clientUserId);

      clientLog({ data: clientUserId, label: 'Client user ID' });
      return clientUserId;
    });

    clientLog({ data: snowplowUserId, label: 'Snowplow user ID' });
  }, [tracker]);

  return null;
};
